/**
 * Critical Issues Panel Component
 *
 * Displays critical issues identified during visibility diagnosis.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, AlertCircle, Info, Bot } from 'lucide-react';
import { CriticalIssue } from '@/services/visibilityDiagnosisService';

interface CriticalIssuesPanelProps {
  issues: CriticalIssue[];
}

const severityConfig = {
  critical: {
    icon: AlertTriangle,
    color: 'text-red-600 dark:text-red-400',
    bgColor: '!bg-red-100 dark:!bg-red-950/50 !border-red-300 dark:!border-red-800',
    textColor: 'text-red-800 dark:text-red-200',
    badge: 'destructive' as const,
  },
  high: {
    icon: AlertCircle,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: '!bg-orange-100 dark:!bg-orange-950/50 !border-orange-300 dark:!border-orange-800',
    textColor: 'text-orange-800 dark:text-orange-200',
    badge: 'default' as const,
  },
  warning: {
    icon: Info,
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: '!bg-yellow-100 dark:!bg-yellow-950/50 !border-yellow-300 dark:!border-yellow-800',
    textColor: 'text-yellow-800 dark:text-yellow-200',
    badge: 'secondary' as const,
  },
};

const categoryLabels: Record<string, string> = {
  session_depth: 'Session Depth',
  entry_points: 'Entry Points',
  robots_txt: 'Robots.txt',
  content_context: 'Content Context',
  coverage: 'Coverage',
};

export function CriticalIssuesPanel({ issues }: CriticalIssuesPanelProps) {
  if (issues.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Critical Issues
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No critical issues detected. Your site's visibility looks healthy!</p>
        </CardContent>
      </Card>
    );
  }

  // Group issues by severity
  const criticalIssues = issues.filter(i => i.severity === 'critical');
  const highIssues = issues.filter(i => i.severity === 'high');
  const warningIssues = issues.filter(i => i.severity === 'warning');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          Critical Issues
          <Badge variant="destructive" className="ml-2">{issues.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {criticalIssues.length > 0 && (
          <div className="space-y-3">
            {criticalIssues.map((issue, index) => {
              const config = severityConfig.critical;
              const Icon = config.icon;
              return (
                <Alert key={`critical-${index}`} className={`${config.bgColor} ${config.textColor}`}>
                  <Icon className={`h-4 w-4 ${config.color}`} />
                  <AlertTitle className="flex items-center gap-2">
                    <Badge variant={config.badge}>Critical</Badge>
                    <Badge variant="outline">{categoryLabels[issue.category] || issue.category}</Badge>
                    {issue.bot && (
                      <span className="text-sm opacity-70 flex items-center gap-1">
                        <Bot className="h-3 w-3" /> {issue.bot}
                      </span>
                    )}
                  </AlertTitle>
                  <AlertDescription className="mt-2">
                    <p>{issue.message}</p>
                    {issue.impact && (
                      <p className="text-sm mt-1 opacity-70">Impact: {issue.impact}</p>
                    )}
                    {issue.rule && (
                      <code className="text-xs bg-black/10 dark:bg-white/10 px-2 py-1 rounded mt-2 block">{issue.rule}</code>
                    )}
                  </AlertDescription>
                </Alert>
              );
            })}
          </div>
        )}

        {highIssues.length > 0 && (
          <div className="space-y-3">
            {highIssues.map((issue, index) => {
              const config = severityConfig.high;
              const Icon = config.icon;
              return (
                <Alert key={`high-${index}`} className={`${config.bgColor} ${config.textColor}`}>
                  <Icon className={`h-4 w-4 ${config.color}`} />
                  <AlertTitle className="flex items-center gap-2">
                    <Badge variant={config.badge}>High</Badge>
                    <Badge variant="outline">{categoryLabels[issue.category] || issue.category}</Badge>
                    {issue.bot && (
                      <span className="text-sm opacity-70 flex items-center gap-1">
                        <Bot className="h-3 w-3" /> {issue.bot}
                      </span>
                    )}
                  </AlertTitle>
                  <AlertDescription className="mt-2">
                    <p>{issue.message}</p>
                    {issue.impact && (
                      <p className="text-sm mt-1 opacity-70">Impact: {issue.impact}</p>
                    )}
                  </AlertDescription>
                </Alert>
              );
            })}
          </div>
        )}

        {warningIssues.length > 0 && (
          <div className="space-y-3">
            {warningIssues.map((issue, index) => {
              const config = severityConfig.warning;
              const Icon = config.icon;
              return (
                <Alert key={`warning-${index}`} className={`${config.bgColor} ${config.textColor}`}>
                  <Icon className={`h-4 w-4 ${config.color}`} />
                  <AlertTitle className="flex items-center gap-2">
                    <Badge variant={config.badge}>Warning</Badge>
                    <Badge variant="outline">{categoryLabels[issue.category] || issue.category}</Badge>
                    {issue.bot && (
                      <span className="text-sm opacity-70 flex items-center gap-1">
                        <Bot className="h-3 w-3" /> {issue.bot}
                      </span>
                    )}
                  </AlertTitle>
                  <AlertDescription className="mt-2">
                    <p>{issue.message}</p>
                    {issue.impact && (
                      <p className="text-sm mt-1 opacity-70">Impact: {issue.impact}</p>
                    )}
                  </AlertDescription>
                </Alert>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
